import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUpload,
  FaFileAlt,
  FaTrash,
  FaCheckCircle,
  FaUserShield,
  FaUserTie,
} from "react-icons/fa";

const API = "https://nissi-india.com/api";

const TenderSuccess = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [contractId, setContractId] = useState("");
  const [tenderId, setTenderId] = useState("");

  // from backend
  const [tenderSuccessId, setTenderSuccessId] = useState(null);
  const [authorizedBy, setAuthorizedBy] = useState("");
  const [approvedBy, setApprovedBy] = useState("");

  const [form, setForm] = useState({
    loiStatus: null,
    emdStatus: null,
    emdDate: "",
    emdReason: "",
    performanceGuaranteeStatus: null,
    performanceGuarantee: "",
    mobilisationAdvanceStatus: null,
    mobilisationAdvance: "",
    retentionGuaranteeStatus: null,
    retentionGuarantee: "",
    materialsSuppliedStatus: null,
    materialsSupplied: "",
  });

  const [loiDocs, setLoiDocs] = useState([]); // { fileName, url }

  // approval checkbox UI
  const [approveChecked, setApproveChecked] = useState(false);
  const [mdApproveChecked, setMdApproveChecked] = useState(false);

  const isAuthorized = !!authorizedBy;
  const isMdApproved = !!approvedBy;

  const isReadOnly = useMemo(() => isMdApproved, [isMdApproved]);

  const canSubmit = useMemo(() => {
    if (isAuthorized) return false;
    if (isMdApproved) return false;
    return true;
  }, [isAuthorized, isMdApproved]);

  // helpers
  const update = (patch) => setForm((p) => ({ ...p, ...patch }));

  const getUserId = () => {
    const v = sessionStorage.getItem("userId") || localStorage.getItem("userId") || "";
    const n = Number(v || 0);
    return Number.isFinite(n) ? n : 0;
  };

  const loadView = async (tid, cid) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/tenderSuccess/view`, {
        params: { tenderId: tid, contractId: cid },
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load");
        return;
      }

      const d = res.data.data || {};

      setTenderSuccessId(d.tenderSuccessId ?? null);
      setAuthorizedBy(d.authorizedBy || "");
      setApprovedBy(d.approvedBy || "");

      setForm({
        loiStatus: d.loiStatus ?? null,
        emdStatus: d.emdStatus ?? null,
        emdDate: d.emdDate || "",
        emdReason: d.emdReason || "",
        performanceGuaranteeStatus: d.performanceGuaranteeStatus ?? null,
        performanceGuarantee: d.performanceGuarantee || "",
        mobilisationAdvanceStatus: d.mobilisationAdvanceStatus ?? null,
        mobilisationAdvance: d.mobilisationAdvance || "",
        retentionGuaranteeStatus: d.retentionGuaranteeStatus ?? null,
        retentionGuarantee: d.retentionGuarantee || "",
        materialsSuppliedStatus: d.materialsSuppliedStatus ?? null,
        materialsSupplied: d.materialsSupplied || "",
      });

      setLoiDocs(Array.isArray(d.loiDocs) ? d.loiDocs : []);

      setApproveChecked(!!d.authorizedBy);
      setMdApproveChecked(!!d.approvedBy);
    } catch (err) {
      console.error("view load error:", err);
      toast.error("Failed to load view");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const cid = urlParams.get("contractId") || "";
    const tid = urlParams.get("tenderId") || "";

    setContractId(cid);
    setTenderId(tid);

    if (tid) loadView(tid, cid);
  }, []);

  const onBack = () => {
    window.history.back();
  };

  const validate = () => {
    if (form.loiStatus === null) {
      toast.error("Select LOI received Yes/No");
      return false;
    }

    if (form.emdStatus === null) {
      toast.error("Select EMD refunded Yes/No");
      return false;
    }

    if (form.emdStatus === true && !form.emdDate) {
      toast.error("EMD Date is required when EMD refunded = Yes");
      return false;
    }

    if (form.emdStatus === false && !String(form.emdReason || "").trim()) {
      toast.error("Reason is required when EMD refunded = No");
      return false;
    }

    return true;
  };

  const onAddLoiFile = async (file) => {
    try {
      if (!file) return;
      if (isReadOnly) return;

      const fd = new FormData();
      fd.append("file", file);

      setLoading(true);

      const res = await axios.post(`${API}/tenderSuccess/upload/loi`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success) {
        const filename = res.data.data?.filename;
        const url = res.data.data?.url;

        setLoiDocs((p) => {
          if (!filename) return p;
          if (p.some((x) => x.fileName === filename)) return p;
          return [...p, { fileName: filename, url }];
        });

        toast.success("LOI file uploaded");
      } else {
        toast.error(res.data?.message || "Upload failed");
      }
    } catch (err) {
      console.error("LOI upload error:", err);
      toast.error("Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const removeLoi = (idx) => {
    if (isReadOnly) return;
    setLoiDocs((p) => p.filter((_, i) => i !== idx));
  };

  const onSubmit = async () => {
    if (!canSubmit) return;
    if (!validate()) return;

    const userId = getUserId();
    if (!userId) {
      toast.error("loginuserid missing in storage");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        tenderId: Number(tenderId),
        tenderSuccessId: tenderSuccessId || null,
        userId,

        loiStatus: form.loiStatus,
        loiFiles: loiDocs.map((x) => x.fileName).filter(Boolean),

        emdStatus: form.emdStatus,
        emdDate: form.emdStatus ? form.emdDate : "",
        emdReason: form.emdStatus ? "" : form.emdReason,

        performanceGuaranteeStatus: form.performanceGuaranteeStatus,
        performanceGuarantee: form.performanceGuaranteeStatus ? form.performanceGuarantee : "",

        mobilisationAdvanceStatus: form.mobilisationAdvanceStatus,
        mobilisationAdvance: form.mobilisationAdvanceStatus ? form.mobilisationAdvance : "",

        retentionGuaranteeStatus: form.retentionGuaranteeStatus,
        retentionGuarantee: form.retentionGuaranteeStatus ? form.retentionGuarantee : "",

        materialsSuppliedStatus: form.materialsSuppliedStatus,
        materialsSupplied: form.materialsSuppliedStatus ? form.materialsSupplied : "",
      };

      const res = await axios.post(`${API}/tenderSuccess/save`, payload);

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved Successfully");

        const newId = res.data?.data?.tenderSuccessId;
        if (newId) setTenderSuccessId(newId);

        await loadView(tenderId, contractId);
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("save error:", err);
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onAuthorizeApprove = async () => {
    if (isReadOnly || isAuthorized) return;

    if (!approveChecked) {
      toast.error("Please tick Authorized Approval checkbox");
      return;
    }

    if (!tenderSuccessId) {
      toast.error("Please save first before approving");
      return;
    }

    const userId = getUserId();
    if (!userId) {
      toast.error("loginuserid missing in storage");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${API}/tenderSuccess/authorize`, {
        tenderId: Number(tenderId),
        tenderSuccessId: Number(tenderSuccessId),
        userId,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Authorized Approved");
        await loadView(tenderId, contractId);
      } else {
        toast.error(res.data?.message || "Authorize failed");
      }
    } catch (err) {
      console.error("authorize error:", err);
      toast.error("Authorize failed");
    } finally {
      setLoading(false);
    }
  };

  const onMdApprove = async () => {
    if (isReadOnly || isMdApproved) return;

    if (!isAuthorized) {
      toast.error("Authorized approval required before MD approval");
      return;
    }

    if (!mdApproveChecked) {
      toast.error("Please tick MD Approval checkbox");
      return;
    }

    if (!tenderSuccessId) {
      toast.error("TenderSuccessId missing");
      return;
    }

    const userId = getUserId();
    if (!userId) {
      toast.error("loginuserid missing in storage");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(`${API}/tenderSuccess/mdApprove`, {
        tenderId: Number(tenderId),
        tenderSuccessId: Number(tenderSuccessId),
        userId,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "MD Approved");
        await loadView(tenderId, contractId);
        window.history.back();
      } else {
        toast.error(res.data?.message || "MD approve failed");
      }
    } catch (err) {
      console.error("mdApprove error:", err);
      toast.error("MD approve failed");
    } finally {
      setLoading(false);
    }
  };

  const cardClass = `rounded-2xl shadow-md border ${
    isDark ? "bg-gray-900 border-gray-700 text-white" : "bg-white border-gray-200 text-gray-900"
  }`;

  const labelClass = `${isDark ? "text-gray-200" : "text-gray-700"} text-sm font-semibold`;

  const inputClass = `w-full px-3 py-2 border rounded-lg outline-none ${
    isDark
      ? "bg-gray-800 border-gray-700 text-white focus:ring-2 focus:ring-gray-600"
      : "bg-white border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-300"
  } ${isReadOnly ? "opacity-60 cursor-not-allowed" : ""}`;

  const sectionTitle = (icon, text) => (
    <div className="flex items-center gap-2 mb-3">
      <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>{icon}</span>
      <h2 className="text-base font-bold">{text}</h2>
    </div>
  );

  const RadioYesNo = ({ value, onChange, name, disabled }) => (
    <div className="flex items-center gap-6">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={name}
          checked={value === true}
          onChange={() => onChange(true)}
          disabled={disabled}
        />
        <span>Yes</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="radio"
          name={name}
          checked={value === false}
          onChange={() => onChange(false)}
          disabled={disabled}
        />
        <span>No</span>
      </label>
    </div>
  );

  return (
    <ProjectPreOrderLayoutWrapper>
      <ToastContainer />

      <HeaderBar title="Tender Successfull" />

      {(loading || saving) && (
        <div className="mb-4 text-sm opacity-80">{saving ? "Saving..." : "Loading..."}</div>
      )}

      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${
            isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-900"
          } hover:opacity-90`}
        >
          <FaArrowLeft />
          Back
        </button>

        <div className="text-xs opacity-80">
          Contract ID: <span className="font-semibold">{contractId || "-"}</span> {" | "}
          Tender ID: <span className="font-semibold">{tenderId || "-"}</span> {" | "}
          Success ID: <span className="font-semibold">{tenderSuccessId || "-"}</span>
        </div>
      </div>

      <div className={`${cardClass} p-4 mb-6`}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-lg font-bold flex items-center gap-2">
              <FaCheckCircle className="opacity-80" />
              Tender Success Checklist
            </div>
            <div className="text-sm opacity-80 mt-1">
              Save first → Authorized approval → MD approval. After MD approval page becomes view-only.
            </div>
          </div>

          <div className="flex gap-2">
            {isMdApproved ? (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">✅ MD Approved (View Only)</span>
            ) : isAuthorized ? (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">🛡 Authorized (MD Pending)</span>
            ) : (
              <span className="px-3 py-1 rounded-full text-xs font-semibold border">✍ Draft / Not Approved</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${cardClass} p-5`}>
          {sectionTitle(<FaFileAlt />, "Tender Success Details")}

          <div className="mb-5">
            <div className={labelClass}>Whether LOI received</div>
            <div className="mt-2">
              <RadioYesNo
                name="loi"
                value={form.loiStatus}
                disabled={isReadOnly}
                onChange={(v) => update({ loiStatus: v })}
              />
            </div>

            {form.loiStatus === true && (
              <div className="mt-3 border rounded-xl p-3">
                <label className={`flex items-center gap-2 ${isReadOnly ? "opacity-60" : ""}`}>
                  <FaUpload />
                  <span className="text-sm font-semibold">Upload LOI Document</span>
                  <input
                    type="file"
                    className="text-sm"
                    disabled={isReadOnly}
                    onChange={(e) => onAddLoiFile(e.target.files?.[0])}
                  />
                </label>

                <div className="mt-3">
                  <div className="text-xs font-semibold opacity-80 mb-2">Uploaded Files</div>

                  {loiDocs.length === 0 ? (
                    <div className="text-sm opacity-70">No documents added</div>
                  ) : (
                    <ul className="space-y-2">
                      {loiDocs.map((d, idx) => (
                        <li
                          key={`${d.fileName}-${idx}`}
                          className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                            isDark ? "border-gray-700" : "border-gray-200"
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FaFileAlt className="opacity-70" />
                            <button
                              type="button"
                              className="text-left truncate underline text-sm"
                              onClick={() => {
                                if (!d.url) return;
                                window.open(`https://nissi-india.com${d.url}`, "_blank");
                              }}
                            >
                              {d.fileName}
                            </button>
                          </div>

                          {!isReadOnly && (
                            <button
                              type="button"
                              className="px-2 py-1 rounded-lg border hover:opacity-80"
                              onClick={() => removeLoi(idx)}
                              title="Remove"
                            >
                              <FaTrash />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className={labelClass}>Whether EMD refunded</div>
            <div className="mt-2">
              <RadioYesNo
                name="emd"
                value={form.emdStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === true) update({ emdStatus: true, emdReason: "" });
                  else update({ emdStatus: false, emdDate: "" });
                }}
              />
            </div>

            {form.emdStatus === true && (
              <div className="mt-3">
                <div className="text-xs font-semibold opacity-80 mb-1">EMD Date</div>
                <input
                  type="date"
                  value={form.emdDate}
                  disabled={isReadOnly}
                  onChange={(e) => update({ emdDate: e.target.value })}
                  className={inputClass}
                />
              </div>
            )}

            {form.emdStatus === false && (
              <div className="mt-3">
                <div className="text-xs font-semibold opacity-80 mb-1">Reason</div>
                <textarea
                  value={form.emdReason}
                  disabled={isReadOnly}
                  onChange={(e) => update({ emdReason: e.target.value })}
                  className={inputClass}
                  rows={3}
                  placeholder="Enter reason..."
                />
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className={labelClass}>Performance guarantee if any</div>
            <div className="mt-2">
              <RadioYesNo
                name="pg"
                value={form.performanceGuaranteeStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === false) update({ performanceGuaranteeStatus: false, performanceGuarantee: "" });
                  else update({ performanceGuaranteeStatus: true });
                }}
              />
            </div>
            {form.performanceGuaranteeStatus === true && (
              <div className="mt-3">
                <textarea
                  value={form.performanceGuarantee}
                  disabled={isReadOnly}
                  onChange={(e) => update({ performanceGuarantee: e.target.value })}
                  className={inputClass}
                  rows={2}
                  placeholder="Remarks..."
                />
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className={labelClass}>Mobilisation advance if any</div>
            <div className="mt-2">
              <RadioYesNo
                name="ma"
                value={form.mobilisationAdvanceStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === false) update({ mobilisationAdvanceStatus: false, mobilisationAdvance: "" });
                  else update({ mobilisationAdvanceStatus: true });
                }}
              />
            </div>
            {form.mobilisationAdvanceStatus === true && (
              <div className="mt-3">
                <textarea
                  value={form.mobilisationAdvance}
                  disabled={isReadOnly}
                  onChange={(e) => update({ mobilisationAdvance: e.target.value })}
                  className={inputClass}
                  rows={2}
                  placeholder="Remarks..."
                />
              </div>
            )}
          </div>

          <div className="mb-5">
            <div className={labelClass}>Retention guarantee if any</div>
            <div className="mt-2">
              <RadioYesNo
                name="rg"
                value={form.retentionGuaranteeStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === false) update({ retentionGuaranteeStatus: false, retentionGuarantee: "" });
                  else update({ retentionGuaranteeStatus: true });
                }}
              />
            </div>
            {form.retentionGuaranteeStatus === true && (
              <div className="mt-3">
                <textarea
                  value={form.retentionGuarantee}
                  disabled={isReadOnly}
                  onChange={(e) => update({ retentionGuarantee: e.target.value })}
                  className={inputClass}
                  rows={2}
                  placeholder="Remarks..."
                />
              </div>
            )}
          </div>

          <div className="mb-2">
            <div className={labelClass}>Materials supplied if any</div>
            <div className="mt-2">
              <RadioYesNo
                name="ms"
                value={form.materialsSuppliedStatus}
                disabled={isReadOnly}
                onChange={(v) => {
                  if (v === false) update({ materialsSuppliedStatus: false, materialsSupplied: "" });
                  else update({ materialsSuppliedStatus: true });
                }}
              />
            </div>
            {form.materialsSuppliedStatus === true && (
              <div className="mt-3">
                <textarea
                  value={form.materialsSupplied}
                  disabled={isReadOnly}
                  onChange={(e) => update({ materialsSupplied: e.target.value })}
                  className={inputClass}
                  rows={2}
                  placeholder="Remarks..."
                />
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={!canSubmit || saving}
              onClick={onSubmit}
              className={`px-5 py-2 rounded-xl text-white flex items-center gap-2 ${
                canSubmit ? `${buttonClass} ${hoverClass}` : "bg-gray-400 cursor-not-allowed"
              }`}
            >
              <FaSave />
              Submit
            </button>
          </div>
        </div>

        <div className={`${cardClass} p-5`}>
          {sectionTitle(<FaUserShield />, "Approval Workflow")}

          <div className="space-y-4">
            <div className={`p-4 rounded-2xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold flex items-center gap-2">
                    <FaUserShield className="opacity-80" />
                    Authorized Approval
                  </div>
                  <div className="text-sm opacity-80 mt-1">
                    Save first. Once authorized, submit is disabled and MD approval can proceed.
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs opacity-70">Status</div>
                  <div className="text-sm font-semibold">{isAuthorized ? "Approved" : "Pending"}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={approveChecked}
                    disabled={isReadOnly || isAuthorized}
                    onChange={(e) => setApproveChecked(e.target.checked)}
                  />
                  <span className="text-sm">Authorized Approval</span>
                </label>

                <button
                  type="button"
                  disabled={isReadOnly || isAuthorized || !approveChecked}
                  onClick={onAuthorizeApprove}
                  className={`px-4 py-2 rounded-xl text-white flex items-center gap-2 ${
                    !isReadOnly && !isAuthorized && approveChecked
                      ? `${buttonClass} ${hoverClass}`
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaUserShield />
                  Approve
                </button>
              </div>
            </div>

            <div className={`p-4 rounded-2xl border ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-bold flex items-center gap-2">
                    <FaUserTie className="opacity-80" />
                    MD Approval
                  </div>
                  <div className="text-sm opacity-80 mt-1">Enabled only after Authorized approval.</div>
                </div>

                <div className="text-right">
                  <div className="text-xs opacity-70">Status</div>
                  <div className="text-sm font-semibold">{isMdApproved ? "Approved" : "Pending"}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <label className={`flex items-center gap-2 ${!isAuthorized ? "opacity-60" : ""}`}>
                  <input
                    type="checkbox"
                    checked={mdApproveChecked}
                    disabled={isReadOnly || isMdApproved || !isAuthorized}
                    onChange={(e) => setMdApproveChecked(e.target.checked)}
                  />
                  <span className="text-sm">MD Approval</span>
                </label>

                <button
                  type="button"
                  disabled={isReadOnly || isMdApproved || !isAuthorized || !mdApproveChecked}
                  onClick={onMdApprove}
                  className={`px-4 py-2 rounded-xl text-white flex items-center gap-2 ${
                    !isReadOnly && !isMdApproved && isAuthorized && mdApproveChecked
                      ? `${buttonClass} ${hoverClass}`
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
                >
                  <FaUserTie />
                  MD Approve
                </button>
              </div>
            </div>

            <div className="text-xs opacity-70 leading-relaxed">
              <div className="font-semibold mb-1">Rules</div>
              <ul className="list-disc ml-5 space-y-1">
                <li>Submit disabled after Authorized approval.</li>
                <li>After MD approval, page becomes view-only.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default TenderSuccess;